// Shared modal shell + form field wrapper used across route modals
// (employee creation, credential issuance, fob enrollment, etc).
import { useEffect, useLayoutEffect, useRef, type ReactNode } from 'react';

export function ModalShell({
  title,
  testId,
  wide,
  destructive,
  submitting,
  children,
  onCancel,
}: {
  title: string;
  testId?: string;
  wide?: boolean;
  /** When true, a stray backdrop click won't close the dialog —
   * the user has to pick a button, so they can't lose a confirmation
   * partway through a destructive action. */
  destructive?: boolean;
  /** When a submission is in flight, ignore Escape and backdrop
   * clicks so the user can't abandon the modal in mid-network state. */
  submitting?: boolean;
  children: ReactNode;
  onCancel: () => void;
}) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  // useId() would be ideal here for the title id, but we already have
  // testId-namespaced ids elsewhere; build a stable one from the testId
  // when given, otherwise fall back to a generic.
  const titleId = testId ? `${testId}-title` : 'modal-shell-title';
  useEffect(() => {
    panelRef.current?.focus();
  }, []);
  useLayoutEffect(() => {
    function onKey(e: KeyboardEvent) {
      // Escape closes the dialog — except on destructive confirms
      // (don't let a stray Escape silently cancel "permanently remove")
      // and while a submission is in flight (we'd lose the request state).
      if (e.key === 'Escape' && !submitting && !destructive) onCancel();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel, submitting, destructive]);
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      data-testid={testId}
      className="fixed inset-0 z-50 flex items-center justify-center bg-scrim/50"
      onClick={(e) => {
        if (submitting) return;
        if (destructive) return;
        if (submitting) return;
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        className={`bg-surface-container-lowest border border-outline-variant rounded-xl p-6 outline-none ${
          wide ? 'w-[640px]' : 'w-[440px]'
        } max-w-[92vw] max-h-[90vh] overflow-auto`}
      >
        <h2 id={titleId} className="font-headline-sm text-headline-sm text-on-surface mb-4">{title}</h2>
        {children}
      </div>
    </div>
  );
}

export function Field({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1">
      <label
        htmlFor={htmlFor}
        className="block font-label-md text-label-md text-on-surface-variant uppercase"
      >
        {label}
      </label>
      {children}
    </div>
  );
}
